import { fromDate, getLocalTimeZone } from "@internationalized/date";
import type { ColDef, SelectionChangedEvent } from "ag-grid-community";
import dayjs from "dayjs";
import { PlusIcon, PrinterIcon } from "lucide-react";
import React from "react";
import { Autocomplete, type DateValue, useFilter } from "react-aria-components";
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form";

import { trpc } from "@blis/trpc/react";

import { AgGridReact } from "@/components/grid/AgGrid";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DataList,
  DataListItem,
  DataListLabel,
  DataListValue,
} from "@/components/ui/data-list";
import { DatePicker, DatePickerInput } from "@/components/ui/date-picker";
import { FieldError, Input, Label, TextField } from "@/components/ui/field";
import { Heading } from "@/components/ui/heading";
import { DropdownItem, ListBox } from "@/components/ui/list-box";
import { Note } from "@/components/ui/note";
import { Popover } from "@/components/ui/popover";
import { SearchField, SearchInput } from "@/components/ui/search-field";
import { Select, SelectTrigger } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/toast";
import { apiUtils } from "@/context/query";
import { usePrintLabel } from "@/lib/labels";
import { fixLocalDate, format } from "@/lib/utils";
import { mutationResponse } from "@/utils/trpc";
import type {
  WorkflowStep as TWorkflowStep,
  WorkspaceMaterial,
  WorkspaceTestOrderSample,
} from "../../types";
import { workspaceGridTheme } from "../../utils/grid";
import { useWorkspace } from "../WorkspaceProvider";
import { WorkflowHeader } from "./WorkflowHeader";

export function WorkflowInoculation({ step }: { step: TWorkflowStep }) {
  const { instanceMaterials } = useWorkspace();
  if (step.id !== "INOCULATION") {
    return null;
  }

  return (
    <>
      <WorkflowHeader step={step} actions={<PrintLabelsButton />} />
      {instanceMaterials.length > 0 ? (
        <InoculationSummary />
      ) : (
        <InoculationFormFields step={step} />
      )}
    </>
  );
}

function InoculationFormFields({ step }: { step: TWorkflowStep }) {
  const { materials } = useWorkspace();
  return materials.length === 0 ? (
    <Note intent="danger">
      No materials are configured for this test. Please reach out to your
      administrator to continue.
    </Note>
  ) : (
    <MaterialSelectForm materials={materials} step={step} />
  );
}

function InoculationSummary() {
  const materialsUsed = useMaterialsUsed();
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 px-6 py-4">
        {materialsUsed.map((material) => (
          <DataList
            key={`${material.spec.id}-${material.lotNumber}`}
            className="border rounded-lg p-4"
          >
            <DataListItem>
              <DataListLabel>Media</DataListLabel>
              <DataListValue>{material.spec.code}</DataListValue>
            </DataListItem>
            <DataListItem>
              <DataListLabel>Lot #</DataListLabel>
              <DataListValue>{material.lotNumber}</DataListValue>
            </DataListItem>
            <DataListItem>
              <DataListLabel>Expiration</DataListLabel>
              <DataListValue>
                {format(material.expiration, { time: false })}
              </DataListValue>
            </DataListItem>
            {/* <DataListItem>
            <DataListLabel>Samples</DataListLabel>
            <DataListValue>
              {material.numberOfSamples}{" "}
              {material.numberOfSamples === 1 ? "sample" : "samples"}
            </DataListValue>
          </DataListItem> */}
          </DataList>
        ))}
      </div>
      <div className="px-6 py-4 flex-1 ag-theme-blis">
        <SampleGrid />
      </div>
    </>
  );
}

function useMaterialsUsed() {
  const { instanceMaterials } = useWorkspace();

  return instanceMaterials.map((material) => ({
    spec: material.materialSpec,
    lotNumber: material.lotNumber,
    expiration: material.expiration,
  }));
}

function MaterialSelectForm({
  materials,
  step,
}: {
  materials: WorkspaceMaterial[];
  step: TWorkflowStep;
}) {
  const { testOrderSamples, testRun } = useWorkspace();
  const submitExecution = trpc.process.submitInoculation.useMutation(
    mutationResponse({
      errorTitle: "Failed to advance step",
      successMessage: "Process step advanced",
      onSuccess(data) {
        // TODO: print labels
      },
    }),
  );

  const methods = useForm({
    defaultValues: {
      testOrderSamples: testOrderSamples.map((sample) => ({
        testOrderId: sample.testOrderId,
        sampleId: sample.sampleId,
      })),
    },
  });
  // biome-ignore lint/suspicious/noExplicitAny: don't know
  const onSubmit = (data: any) => {
    submitExecution.mutate({
      testRunId: testRun.id,
      data: {
        ...data,
        testOrderSamples: data.testOrderSamples,
      },
    });
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        className="px-6 py-4 space-y-4 flex-1 flex flex-col ag-theme-blis"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {materials.map((material, index) => (
            <div key={material.id} className="border rounded-lg p-4">
              <MaterialSelectionRHF material={material} index={index} />
            </div>
          ))}
        </div>
        {/* {step.template.metadata && <WorkflowMetadata step={step} />} */}
        {/* {step.template.timeFields && <WorkflowTimeFields step={step} />} */}
        <div className="flex-1 flex flex-col">
          <SampleSelectionGridForm />
        </div>
        <Separator />
        <div className="flex items-center justify-end">
          <Button type="submit" isPending={submitExecution.isPending}>
            Submit
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}

function MaterialSelectionRHF({
  material,
  index,
}: {
  material: WorkspaceMaterial;
  index: number;
}) {
  const { testRun } = useWorkspace();
  const utils = trpc.useUtils();
  const { control, setValue, watch } = useFormContext<{
    materialLots: number[];
  }>();
  const [createdLots, setCreatedLots] = React.useState<
    NonNullable<WorkspaceMaterial["lots"]>
  >([]);
  const [searchValue, setSearchValue] = React.useState("");
  const [showExpiredLots, setShowExpiredLots] = React.useState(false);
  const [newLotData, setNewLotData] = React.useState({
    lotNumber: "",
    expiration: null as DateValue | null,
  });
  const expiredLotsQuery = trpc.materials.lots.useQuery(
    {
      materialSpecId: [material.id],
      includeExpired: true,
    },
    {
      enabled: showExpiredLots,
    },
  );

  const currentLotData = watch(`materialLots.${index}`) as
    | string
    | number
    | null;

  const { mutate: createLot, isPending: isCreating } =
    trpc.materials.createLot.useMutation(
      mutationResponse({
        errorTitle: "Failed to create lot",
        successMessage: "Lot created",
        onSuccess: (newLot) => {
          setCreatedLots((lots) => [
            newLot,
            ...lots.filter((lot) => lot.id !== newLot.id),
          ]);
          setValue(`materialLots.${index}`, newLot.id);
          setNewLotData({ lotNumber: "", expiration: null });
          setSearchValue("");
          setShowExpiredLots(false);
          void utils.process.getByTestRunId.invalidate({
            testRunId: testRun.id,
          });
        },
      }),
    );

  const handleCreateLot = () => {
    if (newLotData.lotNumber && newLotData.expiration) {
      createLot({
        materialSpecId: material.id,
        lotNumber: newLotData.lotNumber,
        expiration: newLotData.expiration.toDate(getLocalTimeZone()),
      });
    }
  };

  const availableLots = Array.from(
    new Map(
      [
        ...createdLots,
        ...(material.lots || []),
        ...(expiredLotsQuery.data || []),
      ].map((lot) => [lot.id, lot]),
    ).values(),
  ).sort((a, b) => {
    const expiredDiff =
      Number(isMaterialLotExpired(a.expiration)) -
      Number(isMaterialLotExpired(b.expiration));

    if (expiredDiff !== 0) {
      return expiredDiff;
    }

    return b.id - a.id;
  });
  const selectedLot = availableLots.find((lot) => lot.id === currentLotData);
  const visibleLots = showExpiredLots
    ? availableLots
    : availableLots.filter(
        (lot) =>
          !isMaterialLotExpired(lot.expiration) || lot.id === currentLotData,
      );

  // Use filter for searching lots
  const { contains } = useFilter({ sensitivity: "base" });

  // Filter lots based on search, but always include the selected lot
  const filteredLots = searchValue
    ? visibleLots.filter(
        (lot) =>
          contains(lot.lotNumber, searchValue) || lot.id === currentLotData,
      )
    : visibleLots;

  return (
    <div className="space-y-4 flex-1">
      <Heading level={3}>{material.name}</Heading>
      {/* Lot Selection with Autocomplete */}
      <div className="space-y-4">
        <Controller
          control={control}
          name={`materialLots.${index}`}
          render={({ field }) => (
            <Select
              placeholder="Select or search for a lot"
              selectedKey={field.value}
              onSelectionChange={(key) => {
                field.onChange(key ?? null);
              }}
              isRequired
            >
              <Label>Select Lot</Label>
              <SelectTrigger />
              <Popover
                className="p-0 !max-h-96 min-w-(--trigger-width) flex flex-col overflow-hidden"
                placement="bottom"
              >
                <Autocomplete
                  inputValue={searchValue}
                  onInputChange={setSearchValue}
                >
                  <SearchField className="border-b" aria-label="Search lots">
                    <SearchInput
                      className="border-none outline-none shadow-none rounded-none ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                      placeholder="Search lot numbers..."
                      autoFocus
                    />
                  </SearchField>
                  <ListBox
                    className="outline-hidden p-1 overflow-auto flex-1 scroll-pb-1"
                    renderEmptyState={() => (
                      <span className="p-4 text-center">
                        No lots found matching "{searchValue}"
                      </span>
                    )}
                  >
                    {/* Always show create new option at the top */}

                    {/* Show filtered lots */}
                    {filteredLots.length === 0 && searchValue ? (
                      <DropdownItem
                        id="no-lots"
                        isDisabled
                        textValue="No lots found matching"
                      >
                        No lots found matching "{searchValue}"
                      </DropdownItem>
                    ) : (
                      filteredLots.map((lot) => (
                        <DropdownItem
                          key={lot.id}
                          id={lot.id}
                          textValue={`${lot.lotNumber} (Exp: ${format(lot.expiration, { time: false })})`}
                        >
                          <div className="flex justify-between items-center w-full">
                            <span>{lot.lotNumber}</span>
                            <span
                              className={`text-xs ${isMaterialLotExpired(lot.expiration) ? "text-destructive" : "text-muted-foreground"}`}
                            >
                              Exp: {format(lot.expiration, { time: false })}
                            </span>
                          </div>
                        </DropdownItem>
                      ))
                    )}
                    <DropdownItem id="create-new" textValue="Create new lot">
                      <span className="inline-flex flex-row items-center gap-2">
                        <PlusIcon className="size-4" />
                        Create new lot
                      </span>
                    </DropdownItem>
                  </ListBox>
                  <div className="flex items-center justify-between border-t bg-muted/30 px-3 py-2">
                    <Checkbox
                      isSelected={showExpiredLots}
                      onChange={setShowExpiredLots}
                    >
                      Show expired lots
                    </Checkbox>
                    {expiredLotsQuery.isFetching && (
                      <span className="text-xs text-muted-foreground">
                        Loading...
                      </span>
                    )}
                  </div>
                </Autocomplete>
              </Popover>
              <FieldError />
            </Select>
          )}
        />

        {/* Show selected lot details */}
        {selectedLot && currentLotData !== "create-new" && (
          <div className="rounded-md bg-muted/50 p-3 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Lot Number:</span>
              <span className="font-medium">{selectedLot.lotNumber}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Expiration:</span>
              <span
                className={`font-medium ${isMaterialLotExpired(selectedLot.expiration) ? "text-destructive" : ""}`}
              >
                {format(selectedLot.expiration, { time: false })}
              </span>
            </div>
          </div>
        )}

        {/* Create new lot form */}
        {currentLotData === "create-new" && (
          <div className="space-y-3 rounded-md border border-primary/20 p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium">Create New Lot</h4>
              <Button
                variant="ghost"
                size="sm"
                onPress={() => {
                  setNewLotData({ lotNumber: "", expiration: null });
                  setSearchValue("");
                  setValue(`materialLots.${index}`, 0);
                }}
                className="h-6 px-2 text-xs"
              >
                Cancel
              </Button>
            </div>

            <TextField
              value={newLotData.lotNumber}
              onChange={(value) =>
                setNewLotData((prev) => ({ ...prev, lotNumber: value }))
              }
              aria-label="Enter new lot number"
            >
              <Label>Lot Number</Label>
              <Input placeholder="Enter lot number" />
            </TextField>

            <DatePicker
              value={newLotData.expiration}
              onChange={(date) =>
                setNewLotData((prev) => ({ ...prev, expiration: date }))
              }
              hideTimeZone
              aria-label="Select expiration date"
              granularity="day"
              minValue={fromDate(new Date(), getLocalTimeZone())}
            >
              <Label>Expiration Date</Label>
              <DatePickerInput />
            </DatePicker>

            <Button
              className="w-full"
              size="sm"
              onPress={handleCreateLot}
              isDisabled={
                !newLotData.lotNumber || !newLotData.expiration || isCreating
              }
            >
              {isCreating ? "Creating..." : "Create Lot"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function isMaterialLotExpired(expiration: Date) {
  return dayjs(fixLocalDate(expiration)).isBefore(dayjs(), "day");
}

function SampleSelectionGridForm() {
  const { control } = useFormContext<{
    testOrderSamples: {
      testOrderId: number;
      sampleId: number;
    }[];
  }>();

  return (
    <Controller
      control={control}
      name="testOrderSamples"
      render={({ field }) => (
        <SampleGrid
          selectedSamples={field.value}
          onSelectionChange={field.onChange}
        />
      )}
    />
  );
}

function SampleGrid({
  selectedSamples,
  onSelectionChange,
}: {
  selectedSamples?: {
    testOrderId: number;
    sampleId: number;
  }[];
  onSelectionChange?: (
    samples: {
      testOrderId: number;
      sampleId: number;
    }[],
  ) => void;
}) {
  const { testOrderSamples } = useWorkspace();
  const [gridReady, setGridReady] = React.useState(false);
  const gridRef = React.useRef<AgGridReact>(null);

  const rowSelection = React.useMemo(() => {
    return onSelectionChange ? { mode: "multiRow" as const } : undefined;
  }, [onSelectionChange]);

  const setSelectedSamples = React.useCallback(
    ({ selectedNodes }: SelectionChangedEvent<WorkspaceTestOrderSample>) => {
      onSelectionChange?.(
        selectedNodes
          ?.filter((node) => !!node.data)
          .map((node) => ({
            // biome-ignore lint/style/noNonNullAssertion: it's FINE
            testOrderId: node.data!.testOrderId,
            // biome-ignore lint/style/noNonNullAssertion: it's FINE
            sampleId: node.data!.sampleId,
          })) ?? [],
      );
    },
    [onSelectionChange],
  );

  React.useEffect(() => {
    if (!gridRef.current?.api || !gridReady) return;

    gridRef.current.api.forEachNode((node) => {
      const shouldSelect = selectedSamples?.some(
        (row) =>
          row.sampleId === node.data.sampleId &&
          row.testOrderId === node.data.testOrderId,
      );
      node.setSelected(shouldSelect ?? false, false, "api");
    });
  }, [selectedSamples, gridReady]);

  const onGridReady = React.useCallback(() => {
    setGridReady(true);
  }, []);

  const columnDefs = React.useMemo(() => {
    return [
      { field: "sample.displayId", headerName: "Sample" },
      { field: "sample.tube.name", headerName: "Container" },
      { field: "sample.accessionId", headerName: "Accession" },
    ] satisfies ColDef<WorkspaceTestOrderSample>[];
  }, []);
  return (
    <AgGridReact
      ref={gridRef}
      getRowId={(row) => `${row.data.testOrderId}-${row.data.sampleId}`}
      columnDefs={columnDefs}
      rowData={testOrderSamples}
      rowSelection={rowSelection}
      theme={workspaceGridTheme}
      onSelectionChanged={setSelectedSamples}
      onGridReady={onGridReady}
      className="border rounded-lg overflow-hidden min-h-[200px]"
    />
  );
}

function PrintLabelsButton() {
  const { instanceMaterials, testOrderSamples } = useWorkspace();
  const [isPrinting, setIsPrinting] = React.useState(false);
  const { printMaterialLabels } = usePrintLabel();

  const onPrintLabels = React.useCallback(async () => {
    try {
      setIsPrinting(true);
      const samples = await apiUtils.samples.forLabels.fetch(
        {
          ids: testOrderSamples.map(({ sample }) => sample.id),
        },
        { retry: false },
      );
      await printMaterialLabels(
        samples,
        instanceMaterials.map((material) => ({
          code: material.materialSpec.code,
          lotNumber: material.lotNumber,
          expiration: format(material.expiration, { time: false }),
        })),
      );
    } catch (error) {
      toast.error("Failed to print labels");
    } finally {
      setIsPrinting(false);
    }
  }, [testOrderSamples, instanceMaterials, printMaterialLabels]);

  if (instanceMaterials.length === 0) {
    return null;
  }

  return (
    <Button variant="outline" onPress={onPrintLabels} isPending={isPrinting}>
      <PrinterIcon />
      Print labels
    </Button>
  );
}
